// hooks/useProducts.js
// Products Hook - Synchronized with Mobile App
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiService } from "../services/api";
import { translateProducts, translateProduct } from "../utils/translateData";

// Get all products with filters
export const useProducts = (filters = {}) => {
  return useQuery({
    queryKey: ["products", filters],
    queryFn: () => apiService.getProducts(filters),
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    select: (response) => {
      // Handle various API response formats
      let products = [];
      if (Array.isArray(response)) {
        products = response;
      } else if (Array.isArray(response?.data)) {
        products = response.data;
      } else if (Array.isArray(response?.products)) {
        products = response.products;
      } else if (Array.isArray(response?.data?.products)) {
        products = response.data.products;
      }
      
      // Translate products
      const translatedProducts = translateProducts(products);
      
      return {
        products: translatedProducts,
        pagination: response?.pagination || null,
        total: response?.total || translatedProducts.length,
      };
    },
  });
};

// Get single product by ID
export const useProduct = (id) => {
  return useQuery({
    queryKey: ["product", id],
    queryFn: () => apiService.getProduct(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    select: (data) => {
      const product = data?.data || null;
      return product ? translateProduct(product) : null;
    },
  });
};

// Search products
export const useSearchProducts = (query, params = {}) => {
  return useQuery({
    queryKey: ["products", "search", query, params],
    queryFn: () => apiService.searchProducts(query, params),
    enabled: !!query && query.trim().length > 0, // Enable for any non-empty query
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    select: (response) => {
      // Handle various API response formats
      let products = [];
      if (Array.isArray(response)) {
        products = response;
      } else if (Array.isArray(response?.data)) {
        products = response.data;
      } else if (Array.isArray(response?.products)) {
        products = response.products;
      } else if (Array.isArray(response?.data?.products)) {
        products = response.data.products;
      }
      
      // Translate products
      const translatedProducts = translateProducts(products);
      
      return {
        products: translatedProducts,
        pagination: response?.pagination || null,
        total: response?.total || translatedProducts.length,
      };
    },
  });
};

// Get search suggestions
export const useSearchSuggestions = (query) => {
  return useQuery({
    queryKey: ["products", "suggestions", query],
    queryFn: () => apiService.getSearchSuggestions(query),
    enabled: !!query && query.length >= 2,
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    select: (data) => data?.data || [],
  });
};

// Get featured/recommended products
export const useFeaturedProducts = () => {
  return useQuery({
    queryKey: ["products", "featured"],
    queryFn: () => apiService.getFeaturedProducts(),
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    select: (data) => {
      const products = data?.data || [];
      return translateProducts(products);
    },
  });
};

// Get flash sales
export const useFlashSales = () => {
  return useQuery({
    queryKey: ["products", "flash-sales"],
    queryFn: () => apiService.getFlashSales(),
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    select: (data) => {
      const products = data?.data || [];
      return translateProducts(products);
    },
  });
};

// Get products by category
export const useProductsByCategory = (categoryId, params = {}) => {
  return useQuery({
    queryKey: ["products", "category", categoryId, params],
    queryFn: () => apiService.getProductsByCategory(categoryId, params),
    enabled: !!categoryId,
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    select: (response) => {
      // Handle various API response formats
      let products = [];
      if (Array.isArray(response)) {
        products = response;
      } else if (Array.isArray(response?.data)) {
        products = response.data;
      } else if (Array.isArray(response?.products)) {
        products = response.products;
      } else if (Array.isArray(response?.data?.products)) {
        products = response.data.products;
      }
      
      // Translate products
      const translatedProducts = translateProducts(products);
      
      return {
        products: translatedProducts,
        pagination: response?.pagination || null,
        total: response?.total || translatedProducts.length,
      };
    },
  });
};

// Get products by seller
export const useProductsBySeller = (sellerId, params = {}) => {
  return useQuery({
    queryKey: ["products", "seller", sellerId, params],
    queryFn: () => apiService.getProductsBySeller(sellerId, params),
    enabled: !!sellerId,
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    select: (data) => {
      const products = data?.data || [];
      return {
        products: translateProducts(products),
        pagination: data?.pagination || null,
      };
    },
  });
};

// Get product reviews
export const useProductReviews = (productId) => {
  return useQuery({
    queryKey: ["products", productId, "reviews"],
    queryFn: () => apiService.getProductReviews(productId),
    enabled: !!productId,
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    select: (data) => data?.data || [],
  });
};

// Create product review
export const useCreateReview = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, reviewData }) =>
      apiService.createProductReview(productId, reviewData),
    onSuccess: (_, { productId }) => {
      queryClient.invalidateQueries({ queryKey: ["products", productId, "reviews"] });
      queryClient.invalidateQueries({ queryKey: ["product", productId] });
    },
  });
};

// Place bid on product
export const usePlaceBid = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, bidData }) =>
      apiService.placeBid(productId, bidData),
    onSuccess: (_, { productId }) => {
      queryClient.invalidateQueries({ queryKey: ["product", productId] });
      queryClient.invalidateQueries({ queryKey: ["bids"] });
    },
  });
};

export default useProducts;

